import { Cable, Channel } from '@anycable/core'
import { obtainCable } from '@/lib/utils/anycable'
import { ChannelMessage } from './types'

const IDLE_TIMEOUT = 30 * 60 * 1000 // 30 minutes

type CreateChannelOptions = {
  channelName: string
  channelToken: string
  onMessage: (message: ChannelMessage) => void
  onPerformFail: () => void
}

const CABLE_RECONNECT_RETRIES = 3
const CABLE_RECONNECT_RETRY_DELAY = 5000

export default function createChannel({ channelName, channelToken, onMessage, onPerformFail }: CreateChannelOptions) {
  let cable: Cable | null = null
  let channel: Channel | null = null
  let pendingPerforms: { action: string; payload: unknown }[] = []
  let idleTimeout: ReturnType<typeof setTimeout> | null = null

  function resetIdleTimeout() {
    if (idleTimeout) clearTimeout(idleTimeout)
    idleTimeout = setTimeout(() => {
      channel?.disconnect()
      channel = null
    }, IDLE_TIMEOUT)
  }

  async function ensureSubscribedWithRetries() {
    if (!channel || !cable) throw new Error('not initialized')

    let retriesTimes = CABLE_RECONNECT_RETRIES
    while (true) {
      retriesTimes--
      try {
        await cable.connect()
        break
      } catch (e) {
        if (retriesTimes <= 0) throw e
        await new Promise(resolve => setTimeout(resolve, CABLE_RECONNECT_RETRY_DELAY))
      }
    }
  }

  return {
    perform: (action: string, payload: unknown, ignoreFailure?: boolean) => {
      if (!cable) cable = obtainCable()
      if (!cable) return ignoreFailure ? undefined : onPerformFail()

      if (!channel) {
        channel = cable.subscribeTo(channelName, { channel_token: channelToken })
        channel.on('message', msg => {
          resetIdleTimeout()
          onMessage(msg as ChannelMessage)
        })
        channel.on('connect', () => {
          for (const perform of pendingPerforms) {
            channel?.perform(perform.action, perform.payload)
          }
          pendingPerforms = []
          resetIdleTimeout()
        })
      }

      if (channel.state !== 'connected') {
        pendingPerforms.push({ action, payload })

        ensureSubscribedWithRetries().catch(() => {
          pendingPerforms = []
          if (!ignoreFailure) onPerformFail()
        })
      } else {
        channel.perform(action, payload)
        resetIdleTimeout()
      }
    },
    destroy: () => {
      if (channel) channel.disconnect()
      if (idleTimeout) clearTimeout(idleTimeout)
    },
  }
}
